/**
 * Core-100 R2 — CORE-T: a school issues and revokes attestations for ITS students. The database
 * decides who may attest whom (`issue_student_verification`: staff of the school, an approved
 * student of the school) and revocation keeps the record (`revoke_student_verification`, a reason
 * is required). This page only offers those two acts and shows the record.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useMySchoolMembership } from '@/hooks/useSchoolApi';

export const VERIFICATION_TYPES = ['enrollment', 'attendance', 'skills', 'completion', 'recommendation'] as const;

interface Student { user_id: string; display_name: string }
interface Verification {
  id: string; student_user_id: string; verification_type: string; status: string; notes: string | null;
  verified_at: string; revoked_at: string | null; revoke_reason: string | null;
}

export function describeIssueError(message: string): string {
  if (message.includes('NOT_A_STUDENT')) return 'That person is not an approved student of this school.';
  // CORE-T R4w2: notes are bounded server-side (1000 characters).
  if (message.includes('ATTESTATION_NOTES_TOO_LONG')) return 'The note is too long (at most 1000 characters).';
  return 'The attestation could not be recorded.';
}

interface EducationEntry {
  id: string; school_name: string; program: string | null; start_date: string | null; end_date: string | null;
  bound_verification_id: string | null; bound_to_this_school: boolean;
}

export function describeBindError(message: string): string {
  if (message.includes('EDUCATION_ALREADY_ATTESTED')) return "Another school's attestation is already linked to that entry.";
  if (message.includes('EDUCATION_BINDING_WRONG_INSTITUTION')) return 'That entry names a different institution.';
  if (message.includes('SCHOOL_STAFF_ONLY') || message.includes('NOT_AUTHORISED')) return "Only this school's administrators can link attestations.";
  return 'The attestation could not be linked.';
}

/**
 * Core-100 R4 — CORE-M: link a standing attestation to the specific education claim it attests. The server shows a
 * school administrator only the student's entries that NAME this school (`school_student_education`) and refuses a
 * binding over another school's standing attestation or to an entry naming another institution
 * (`bind_school_attestation`). The claim stays the student's own; linking never edits it.
 */
export function EducationLinkPanel({ schoolId, studentId, verificationId }: { schoolId: string; studentId: string; verificationId: string }) {
  const qc = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const entries = useQuery({
    queryKey: ['school-student-education', schoolId, studentId],
    queryFn: async (): Promise<EducationEntry[]> => {
      const { data, error: e } = await supabase.rpc('school_student_education' as never, { p_school_id: schoolId, p_student_user_id: studentId } as never);
      if (e) throw e;
      return ((data ?? []) as unknown) as EducationEntry[];
    },
  });
  const link = async (educationId: string) => {
    setMessage(null);
    const { data, error: e } = await supabase.rpc('bind_school_attestation' as never, { p_education_id: educationId, p_verification_id: verificationId } as never);
    if (e) { setMessage(describeBindError(e.message)); return; }
    setMessage(data === 'NOT_VERIFIED' ? 'This attestation has been withdrawn and cannot be linked.' : 'Linked.');
    void qc.invalidateQueries({ queryKey: ['school-student-education', schoolId, studentId] });
  };
  if (entries.isError) return <p className="text-xs text-muted-foreground">{describeBindError((entries.error as Error).message)}</p>;
  if (entries.isLoading) return null;
  return (
    <div className="mt-1 space-y-1 rounded border p-2" data-testid="education-link-panel">
      {(entries.data ?? []).length === 0 && (
        <p className="text-xs text-muted-foreground">The student has no education entry naming this school.</p>
      )}
      {(entries.data ?? []).map((e) => (
        <div key={e.id} className="flex items-center justify-between gap-2 text-xs">
          <span>{e.school_name}{e.program ? ` · ${e.program}` : ''}{e.start_date ? ` · from ${e.start_date}` : ''}</span>
          {e.bound_verification_id === verificationId ? <Badge variant="default">Linked</Badge>
            : e.bound_verification_id && !e.bound_to_this_school ? <Badge variant="outline">Attested by another school</Badge>
            : <Button size="sm" variant="outline" onClick={() => void link(e.id)}>Link</Button>}
        </div>
      ))}
      {message && <p className="text-xs" role="status">{message}</p>}
    </div>
  );
}

export default function StudentVerificationsManager() {
  const qc = useQueryClient();
  const { data: membership } = useMySchoolMembership();
  const schoolId: string | undefined =
    (membership as { schools?: { id?: string } } | null | undefined)?.schools?.id ?? membership?.school_id;
  const [student, setStudent] = useState('');
  const [type, setType] = useState<string>('completion');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState<string | null>(null);

  const students = useQuery({
    queryKey: ['school-students', schoolId],
    enabled: !!schoolId,
    queryFn: async (): Promise<Student[]> => {
      const { data, error: e } = await supabase.rpc('school_students' as never, { p_school_id: schoolId } as never);
      if (e) throw e;
      return ((data ?? []) as unknown) as Student[];
    },
  });
  const verifications = useQuery({
    queryKey: ['school-verifications', schoolId],
    enabled: !!schoolId,
    queryFn: async (): Promise<Verification[]> => {
      const { data, error: e } = await supabase
        .from('student_verifications')
        .select('id, student_user_id, verification_type, status, notes, verified_at, revoked_at, revoke_reason')
        .eq('school_id', schoolId!)
        .order('verified_at', { ascending: false });
      if (e) throw e;
      return (data ?? []) as Verification[];
    },
  });
  const nameOf = (id: string) => students.data?.find((s) => s.user_id === id)?.display_name ?? 'A former student';

  const issue = async () => {
    setError(null);
    const { error: e } = await supabase.rpc('issue_student_verification' as never, {
      p_school_id: schoolId, p_student_user_id: student, p_type: type, p_notes: notes,
    } as never);
    if (e) { setError(describeIssueError(e.message)); return; }
    setNotes('');
    void qc.invalidateQueries({ queryKey: ['school-verifications', schoolId] });
  };
  const revoke = async (id: string) => {
    const reason = window.prompt('Why is this attestation being revoked? (required)')?.trim();
    if (!reason) return;
    const { error: e } = await supabase.rpc('revoke_student_verification' as never, { p_verification_id: id, p_reason: reason } as never);
    if (e) { setError('The attestation could not be revoked.'); return; }
    void qc.invalidateQueries({ queryKey: ['school-verifications', schoolId] });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Attest a student</CardTitle>
          <CardDescription>Only approved students of this school can be attested. The attestation records who issued it.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 items-end">
          <Select value={student} onValueChange={setStudent}>
            <SelectTrigger className="w-56" aria-label="Student"><SelectValue placeholder="Choose a student" /></SelectTrigger>
            <SelectContent>
              {(students.data ?? []).map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44" aria-label="Attestation type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VERIFICATION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="w-64" placeholder="What is being attested (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Button onClick={() => void issue()} disabled={!student || !schoolId}>Attest</Button>
          {error && <p className="w-full text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Attestations issued</CardTitle></CardHeader>
        <CardContent>
          <ul className="space-y-2" data-testid="school-attestations">
            {(verifications.data ?? []).map((v) => (
              <li key={v.id} className="text-sm">
                <div className="flex items-center justify-between gap-2">
                <span>
                  {nameOf(v.student_user_id)} · {v.verification_type}
                  {v.notes ? ` · ${v.notes}` : ''}
                  {' '}<Badge variant={v.status === 'revoked' ? 'secondary' : 'default'}>{v.status}</Badge>
                  {v.status === 'revoked' && v.revoke_reason ? <span className="text-muted-foreground"> — {v.revoke_reason}</span> : null}
                </span>
                {v.status !== 'revoked' && (
                  <span className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setLinking(linking === v.id ? null : v.id)}>Link to education entry</Button>
                    <Button size="sm" variant="outline" onClick={() => void revoke(v.id)}>Revoke</Button>
                  </span>
                )}
                </div>
                {linking === v.id && schoolId && (
                  <EducationLinkPanel schoolId={schoolId} studentId={v.student_user_id} verificationId={v.id} />
                )}
              </li>
            ))}
            {verifications.data && verifications.data.length === 0 && <li className="text-muted-foreground text-sm">None yet.</li>}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
